import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Save, History, RotateCcw, RefreshCw, CheckCircle2 } from 'lucide-react';
import { API_URL, authHeaders, formatTs, BOT_LABELS } from './ops_helpers';

const KNOWN_BOTS = ['hyperliquid', 'ftmo'];

/**
 * Editor de Config Remota: edita los parametros de cada bot desde el dashboard.
 * Versiona cada cambio (bot_configs) y permite rollback. Los bots hacen polling
 * de GET /api/ops/config/{bot_id} y reaplican cuando cambia la version.
 */
export function BotConfigEditor({ bots = [], isAdmin = false }) {
  const botIds = (bots && bots.length ? bots.map((b) => b.bot_id) : KNOWN_BOTS)
    .filter((v, i, a) => a.indexOf(v) === i);

  const [selectedBot, setSelectedBot] = useState(botIds[0] || 'hyperliquid');
  const [activeConfig, setActiveConfig] = useState(null);
  const [paramsText, setParamsText] = useState('{}');
  const [note, setNote] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async (botId) => {
    if (!botId) return;
    setLoading(true);
    try {
      const [aRes, hRes] = await Promise.all([
        fetch(`${API_URL}/api/ops/config/${botId}/admin`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/ops/config/${botId}/history`, { headers: authHeaders() }),
      ]);
      const aData = aRes.ok ? await aRes.json() : { active_config: null };
      const hData = hRes.ok ? await hRes.json() : { versions: [] };
      setActiveConfig(aData.active_config);
      setHistory(hData.versions || []);
      const params = aData.active_config?.params || {};
      setParamsText(JSON.stringify(params, null, 2));
    } catch (e) {
      toast.error(`Error cargando config: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(selectedBot); }, [selectedBot, loadAll]);

  const handleSave = async () => {
    let parsed;
    try {
      parsed = JSON.parse(paramsText);
    } catch (e) {
      toast.error(`JSON invalido: ${e.message}`);
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || Object.keys(parsed).length === 0) {
      toast.error('Los params deben ser un objeto JSON no vacio');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/ops/config/${selectedBot}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ params: parsed, note: note || null }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      toast.success(`Guardada v${data.config.version} (activa). Los bots la tomaran en el proximo polling.`);
      setNote('');
      await loadAll(selectedBot);
    } catch (e) {
      toast.error(`Error guardando: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (version) => {
    try {
      const r = await fetch(`${API_URL}/api/ops/config/${selectedBot}/activate/${version}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Rollback a v${version} aplicado`);
      await loadAll(selectedBot);
    } catch (e) {
      toast.error(`Error en rollback: ${e.message}`);
    }
  };

  if (!isAdmin) {
    return (
      <Card className="bg-zinc-900/60 border-zinc-800" data-testid="config-no-admin">
        <CardContent className="py-8 text-center text-zinc-400">
          Acceso restringido. Solo administradores pueden editar la config de los bots.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="bot-config-editor">
      {/* Selector de bot */}
      <div className="flex flex-wrap items-center gap-2">
        {botIds.map((id) => (
          <Button
            key={id}
            data-testid={`config-bot-${id}`}
            variant={selectedBot === id ? 'default' : 'outline'}
            size="sm"
            className={selectedBot === id
              ? 'bg-violet-600 hover:bg-violet-500'
              : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'}
            onClick={() => setSelectedBot(id)}
          >
            {BOT_LABELS[id] || id}
          </Button>
        ))}
        <Button
          data-testid="config-refresh"
          variant="ghost"
          size="sm"
          className="ml-auto text-zinc-400 hover:text-zinc-100"
          onClick={() => loadAll(selectedBot)}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Recargar
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Editor */}
        <Card className="bg-zinc-900/60 border-zinc-800 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-zinc-100 flex items-center gap-2">
              Parametros · {BOT_LABELS[selectedBot] || selectedBot}
              {activeConfig ? (
                <Badge className="bg-emerald-600/20 text-emerald-300 border-emerald-700/40" data-testid="config-active-badge">
                  v{activeConfig.version} activa
                </Badge>
              ) : (
                <Badge className="bg-amber-600/20 text-amber-300 border-amber-700/40" data-testid="config-none-badge">
                  sin config aun
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="text-zinc-500">
              Edita el JSON y guarda una nueva version. Se activa al instante; el bot la aplica en su proximo polling.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              data-testid="config-params-textarea"
              value={paramsText}
              onChange={(e) => setParamsText(e.target.value)}
              spellCheck={false}
              className="font-mono text-xs min-h-[320px] bg-zinc-950 border-zinc-800 text-zinc-200"
            />
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">Nota de cambio (opcional)</Label>
              <Input
                data-testid="config-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="ej. subir RR a 2.5, quitar SOL"
                className="bg-zinc-950 border-zinc-800 text-zinc-200"
              />
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="config-save-btn"
                onClick={handleSave}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-500"
              >
                <Save className="h-4 w-4 mr-1" />
                {saving ? 'Guardando...' : 'Guardar nueva version'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Historial */}
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100 flex items-center gap-2 text-base">
              <History className="h-4 w-4" /> Historial
            </CardTitle>
            <CardDescription className="text-zinc-500">Versiones publicadas. Puedes hacer rollback.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {history.length === 0 ? (
              <div className="py-6 text-center text-zinc-500 text-sm" data-testid="config-history-empty">
                Sin versiones todavia
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400">Ver</TableHead>
                    <TableHead className="text-zinc-400">Fecha</TableHead>
                    <TableHead className="text-zinc-400">Nota</TableHead>
                    <TableHead className="text-zinc-400 text-right">Accion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((v) => (
                    <TableRow key={v.version} className="border-zinc-800" data-testid={`config-history-row-${v.version}`}>
                      <TableCell className="font-mono text-zinc-200">v{v.version}</TableCell>
                      <TableCell className="text-zinc-400 text-xs">{formatTs(v.created_at)}</TableCell>
                      <TableCell className="text-zinc-400 text-xs max-w-[120px] truncate">{v.note || '—'}</TableCell>
                      <TableCell className="text-right">
                        {v.active ? (
                          <span className="inline-flex items-center text-emerald-400 text-xs">
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> activa
                          </span>
                        ) : (
                          <Button
                            data-testid={`config-activate-${v.version}`}
                            variant="outline"
                            size="sm"
                            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-7 px-2"
                            onClick={() => handleActivate(v.version)}
                          >
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Activar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default BotConfigEditor;
